<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>

        <template #main>
            <main-header title="Online Course Catalog" icon="fa-browsers"/>

            <div class="columns mt-6 is-block-touch is-block-widescreen is-block-desktop is-flex-fullhd">
                <div class="column is-8-fullhd course-tabs">

                    <div class="mt-5">
                                <div class="has-text-white" v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading courses...</div>
                                <div v-else>
                                    <b-button type="is-light" outlined class="mb-6" v-if="courseFilter" @click="courseFilter=null"><i class="fas fa-arrow-left mr-3"></i>Show all courses</b-button>

                                    <!-- Detail mode via ?course= (bypasses the audience landing) -->
                                    <template v-if="courseFilter">
                                        <catalog-course
                                            class="mb-5"
                                            :course="course"
                                            v-for="course in sortedCourses"
                                            @enroll="enroll"
                                            :details="true"
                                            v-if="course.id === courseFilter"
                                        />
                                    </template>

                                    <!-- Flat fallback when no category data is available -->
                                    <template v-else-if="!categoryGroups || categoryGroups.length === 0">
                                        <catalog-course
                                            class="mb-5"
                                            :course="course"
                                            v-for="course in sortedCourses"
                                            @enroll="enroll"
                                            :details="false"
                                        />
                                    </template>

                                    <!-- Audience landing: one stacked card per certification,
                                         matching the app's full-width card pattern -->
                                    <div v-else-if="!effectiveAudience">
                                        <div class="audience-tile mb-5" v-for="audience in audiences" :key="'audience-' + audience.id" @click="enterAudience(audience.id)">
                                            <div class="columns">
                                                <div class="column is-narrow is-hidden-mobile">
                                                    <i class="fad fa-file-certificate has-text-primary fa-3x"></i>
                                                </div>
                                                <div class="column">
                                                    <div class="has-text-weight-bold is-size-5 mb-1">{{ audience.title }}</div>
                                                    <div class="meta" v-if="audience.description">{{ audience.description }}</div>
                                                </div>
                                                <div class="column is-3">
                                                    <b-button type="is-primary" expanded>Browse Courses<i class="fas fa-arrow-right ml-3"></i></b-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Audience view: category cards, drilling into one
                                         category's course list (same pattern as the
                                         certification cards above) -->
                                    <div v-else>
                                        <template v-if="selectedCategory === null">
                                            <b-button type="is-light" outlined class="mb-5" v-if="audiences.length > 1" @click="backToAudiences"><i class="fas fa-arrow-left mr-3"></i>All Certifications</b-button>

                                            <h2 class="catalog-heading has-text-white"><i class="fad fa-file-certificate fa-fw mr-3"></i>{{ effectiveAudience.title }}</h2>

                                            <template v-if="effectiveAudience.groups.length">
                                                <div class="audience-tile mb-5" v-for="section in audienceSections" :key="'seccard-' + section.id" @click="enterCategory(section.id)">
                                                    <div class="columns">
                                                        <div class="column is-narrow is-hidden-mobile">
                                                            <i class="fad fa-folder-open has-text-primary fa-2x"></i>
                                                        </div>
                                                        <div class="column">
                                                            <div class="has-text-weight-bold mb-1">{{ section.title }}</div>
                                                            <div class="meta" v-if="section.description">{{ section.description }}</div>
                                                            <div class="meta mt-1"><i class="fad fa-books mr-2"></i>{{ section.count }} {{ section.count === 1 ? 'course' : 'courses' }}</div>
                                                        </div>
                                                        <div class="column is-3">
                                                            <b-button type="is-primary" expanded>Browse<i class="fas fa-arrow-right ml-3"></i></b-button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <catalog-course
                                                    class="mb-5"
                                                    :course="course"
                                                    v-for="course in filteredAudienceCourses"
                                                    :key="'other-' + course.id"
                                                    @enroll="enroll"
                                                    :details="false"
                                                />
                                            </template>
                                        </template>

                                        <!-- Category course list -->
                                        <template v-else>
                                            <b-button type="is-light" outlined class="mb-5" @click="backToCategories"><i class="fas fa-arrow-left mr-3"></i>{{ effectiveAudience.title }}</b-button>

                                            <b-field grouped group-multiline class="audience-filters mb-5" v-if="filterGroups.length">
                                                <b-field :label="group.title" v-for="group in filterGroups" :key="'filter-' + group.id">
                                                    <b-select :value="filterSelections[group.id] || null" @input="setFilter(group.id, $event)">
                                                        <option :value="null">All {{ group.title }}</option>
                                                        <option :value="child.id" v-for="child in group.children" :key="'option-' + child.id">{{ child.title }}</option>
                                                    </b-select>
                                                </b-field>
                                            </b-field>

                                            <div class="has-text-white" v-if="!activeSection"><i class="far fa-info-circle fa-fw mr-3"></i>No courses match the selected filters.</div>
                                            <catalog-category-section v-else :section="activeSection" @enroll="enroll"/>
                                        </template>
                                    </div>
                                </div>
                            </div>

                </div>
                <div class="column is-offset-1-fullhd has-text-white">
                    <hr class="is-hidden-fullhd"/>
                    <div class="">
                        <div class="mb-6">
                            <router-link :to="{name: 'enrollments'}">
                                <b-button type="is-light" outlined><i class="fad fa-chalkboard-teacher mr-3"></i>View Your Enrollments</b-button>
                            </router-link>
                        </div>

                        <div class="mt-6 mb-6 quick">
                            <h5 class="has-text-white mb-3 is-size-6 has-text-weight-bold"><i class="far fa-fw fa-chalkboard-user mr-3"></i>Access a Training Course</h5>
                            <b-field class="ml-4 pl-4">
                                <b-input placeholder="Course Access Code" v-model="trainingCode" expanded v-on:keyup.native.enter="trainingCheck"></b-input>
                                <p class="control">
                                    <b-button type="is-primary is-light" :disabled="!trainingCode" @click="trainingCheck">
                                        <i class="far fa-arrow-right"></i>
                                    </b-button>
                                </p>
                            </b-field>
                        </div>
                    </div>

                </div>
            </div>

            <b-modal v-model="enrollNotify" :width="500" scroll="keep" custom-class="notifyModal">
                <div class="card" v-if="enrollCourse">
                    <div class="card-content">
                        <div class="columns is-gapless">
                            <div class="column is-narrow mr-2">
                                <i class="fas fa-check-circle mr-3 fa-2x has-text-primary"></i>
                            </div>
                            <div class="column mr-3 content">
                                <div style="font-size: 0.8rem; padding: 0.1rem 0 0.3rem">ENROLLMENT COMPLETE</div>
                                <h4 class="has-text-primary">{{ enrollCourse.title }}</h4>

                                <ul class="model-menu">
                                    <li>
                                        <router-link v-if="enrollCourse.new_course" :to="'/course/' + enrollCourse.enroll_id"><i class="fad fa-pen-to-square mr-3 has-text-primary"></i>Open Course<i class="fas fa-arrow-right mr-4 is-pulled-right mt-1"></i></router-link>
                                        <router-link v-else :to="'/legacy/' + enrollCourse.enroll_id"><i class="fad fa-pen-to-square mr-3 has-text-primary"></i>Open Course<i class="fas fa-arrow-right mr-4 is-pulled-right mt-1"></i></router-link>
                                    </li>
                                    <li><a href v-on:click.prevent="enrollNotify=false"><i class="fad fa-browser mr-3 has-text-primary"></i>Continue Browsing Catalog<i class="fas fa-arrow-right mr-4 is-pulled-right mt-1"></i></a></li>
                                    <li>
                                        <router-link to="/enrollments"><i class="fad fa-chalkboard-teacher mr-3 has-text-primary"></i>See Your Course Enrollments<i class="fas fa-arrow-right mr-4 is-pulled-right mt-1"></i></router-link>
                                    </li>
                                </ul>
                            </div>
                        </div>

                    </div>

                </div>
            </b-modal>
        </template>
    </framework>
</template>

<script>
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import {client as http} from "../../http_client";
    import CatalogCourse from "../../components/CatalogCourse";
    import async from "async";
    import CatalogCategorySection from "../../components/course/CatalogCategorySection";
    import {mapActions, mapGetters} from "vuex";

    export default {
        components: {CatalogCourse, CatalogCategorySection, Framework, SidePanelNav, MainHeader},
        data: function() {
            return {
                loading: true,
                courses: [],
                courseFilter: null,
                selectedAudience: null,
                selectedCategory: null,
                filterSelections: {},
                enrollNotify: false,
                enrollCourse: null,
                trainingCode: ''
            };
        },
        computed: {
            ...mapGetters(['enrollments', 'currentUser', 'categoryGroups']),
            sortedCourses() {
                return this.courses.sort((a, b) => (a.title.toUpperCase() > b.title.toUpperCase()) ? 1 : -1);
            },
            // Audiences: one per certification (from categoryGroups) that has at
            // least one visible course, plus a trailing "Other Courses" audience
            // for courses that resolve to no known certification.
            audiences() {
                if (!this.categoryGroups || this.categoryGroups.length === 0) return [];

                const audiences = [];
                const byId = {};
                for (const group of this.categoryGroups) {
                    const audience = {id: group.certification.id, title: group.certification.title, description: group.certification.description || null, groups: group.categories || [], courses: []};
                    byId[audience.id] = audience;
                    audiences.push(audience);
                }

                const other = {id: 'other', title: 'Other Courses', groups: [], courses: []};

                for (const course of this.sortedCourses) {
                    const certIds = [];

                    // A course belongs to an audience via its categories' certification_id
                    if (course.categories && course.categories.length > 0) {
                        for (const cat of course.categories) {
                            if (byId[cat.certification_id] && !certIds.includes(cat.certification_id)) {
                                certIds.push(cat.certification_id);
                            }
                        }
                    } else if (course.configs) {
                        // No categories: fall back to the first non-null
                        // certification_id among the course's configs
                        const config = course.configs.find(c => c.certification_id);
                        if (config && byId[config.certification_id]) {
                            certIds.push(config.certification_id);
                        }
                    }

                    if (certIds.length === 0) {
                        other.courses.push(course);
                    } else {
                        for (const id of certIds) byId[id].courses.push(course);
                    }
                }

                const visible = audiences.filter(a => a.courses.length > 0);
                if (other.courses.length > 0) visible.push(other);
                return visible;
            },
            // The audience being browsed; auto-entered when only one exists
            effectiveAudience() {
                if (this.selectedAudience !== null) {
                    return this.audiences.find(a => a.id === this.selectedAudience) || null;
                }
                if (this.audiences.length === 1) return this.audiences[0];
                return null;
            },
            // Top-level groups of the current audience that act as filter dropdowns
            filterGroups() {
                const audience = this.effectiveAudience;
                if (!audience) return [];
                return audience.groups.filter(g => g.catalog_role === 'filter' && g.children && g.children.length > 0);
            },
            // Audience courses narrowed by the active filter dropdowns (ANDed)
            filteredAudienceCourses() {
                const audience = this.effectiveAudience;
                if (!audience) return [];

                let courses = audience.courses;
                for (const group of this.filterGroups) {
                    const selected = this.filterSelections[group.id];
                    if (!selected) continue;
                    courses = courses.filter(course =>
                        course.categories && course.categories.some(cat => cat.id === selected)
                    );
                }
                return courses;
            },
            // Browse sections (catalog_role 'browse') for the current audience,
            // plus a trailing "More Courses" section for unassigned courses
            audienceSections() {
                const audience = this.effectiveAudience;
                if (!audience) return [];

                const courses = this.filteredAudienceCourses;
                const coursesFor = (categoryId) => courses.filter(course =>
                    course.categories && course.categories.some(cat => cat.id === categoryId)
                );

                const browseGroups = audience.groups.filter(g => g.catalog_role !== 'filter');
                const browseIds = [];
                for (const group of browseGroups) {
                    browseIds.push(group.id);
                    for (const child of (group.children || [])) browseIds.push(child.id);
                }

                const sections = [];
                for (const group of browseGroups) {
                    const children = [];
                    for (const child of (group.children || [])) {
                        const childCourses = coursesFor(child.id);
                        if (childCourses.length > 0) {
                            children.push({id: child.id, title: child.title, count: childCourses.length, courses: childCourses});
                        }
                    }

                    const direct = coursesFor(group.id);
                    if (direct.length === 0 && children.length === 0) continue;

                    let count = direct.length;
                    for (const child of children) count += child.count;

                    sections.push({id: group.id, title: group.title, description: group.description || null, count, courses: direct, children});
                }

                // Courses with no browse-section assignment trail the sections
                const more = courses.filter(course => {
                    if (!course.categories || course.categories.length === 0) return true;
                    return !course.categories.some(cat => browseIds.includes(cat.id));
                });
                if (more.length > 0) {
                    sections.push({id: 'more', title: 'More Courses', description: null, count: more.length, courses: more, children: []});
                }

                return sections;
            },
            // The category being browsed inside the audience; null when its
            // courses are entirely filtered away
            activeSection() {
                if (this.selectedCategory === null) return null;
                return this.audienceSections.find(s => s.id === this.selectedCategory) || null;
            }
        },
        methods: {
            ...mapActions(['getEnrollments', 'getCategories']),
            // Navigation writes the URL; syncFromRoute() reads it back into
            // state, so browser back/forward and refresh keep position
            enterAudience(id) {
                this.$router.push({query: {certification: String(id)}}).catch(() => {});
            },
            backToAudiences() {
                this.$router.push({query: {}}).catch(() => {});
            },
            enterCategory(id) {
                this.$router.push({query: {...this.$route.query, category: String(id)}}).catch(() => {});
            },
            backToCategories() {
                const query = {...this.$route.query};
                delete query.category;
                this.$router.push({query}).catch(() => {});
            },
            syncFromRoute() {
                const q = this.$route.query;

                this.courseFilter = q.course ? parseInt(q.course) : null;

                // 'other' / 'more' are the synthetic non-numeric ids
                const audience = !q.certification ? null
                    : (q.certification === 'other' ? 'other' : parseInt(q.certification));
                const category = !q.category ? null
                    : (q.category === 'more' ? 'more' : parseInt(q.category));

                if (audience !== this.selectedAudience || category !== this.selectedCategory) {
                    this.filterSelections = {};
                }
                this.selectedAudience = audience;
                this.selectedCategory = category;
            },
            setFilter(groupId, value) {
                this.$set(this.filterSelections, groupId, value);
            },
            load: function() {

                async.series([
                    (cb) => {
                        // Category data drives the audience landing/browse views
                        this.getCategories().then(() => cb()).catch(() => cb());
                    },
                    (cb) => {
                        if (!this.enrollments) {
                            this.getEnrollments().then(() => {
                                cb();
                            });
                        } else {
                            cb();
                        }
                    },
                    (cb) => {
                        http.get('app/courses/new').then(response => {
                            for (let i = 0; i < response.data.length; i++) {
                                const course = response.data[i];
                                course.processing = false;

                                // console.log(this.enrollments);

                                for (let j = 0; j < this.enrollments.length; j++) {
                                    const enrollment = this.enrollments[j];
                                    if (course.id === enrollment.courses.id) {
                                        course.enrolled = true;
                                        break;
                                    }
                                }
                            }

                            this.courses = response.data;
                            cb();
                        });
                    },
                    (cb) => {
                        if (this.$route.query.code) {
                            this.trainingCode = this.$route.query.code;
                            this.trainingCheck();
                        }
                        cb();
                    }
                ], err => {
                    this.loading = false;
                });

                this.syncFromRoute();

                // Legacy ?cert= deep links (FinalForms partner flow) targeted the
                // removed Browse by Certification tab; resolve the credential to
                // its certification program and open that audience instead.
                if (this.$route.query.cert) {
                    http.get('app/certs/', {force: true}).then(response => {
                        const cert = response.data.find(c => c.id === parseInt(this.$route.query.cert));
                        if (cert && cert.certification_id) this.enterAudience(cert.certification_id);
                    }).catch(() => {});
                }
            },
            enroll: function(course) {
                this.$router.push({name: 'course-enroll', params: {courseId: course.id}});
            },
            isEnrolled(course) {
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (course.id === enrollment.courses.id) {
                        return true;
                    }
                }
            },
            course: function(id) {
                if (parseInt(this.$route.query.course) !== id) {
                    this.$router.push({path: 'course-catalog', query: {course: id}});
                } else {
                    this.courseFilter = id;
                }
            },
            trainingCheck: function() {
                http.get('app/courses/new/code/' + this.trainingCode, {force: true}).then(response => {
                    let course = response.data;
                    for (let j = 0; j < this.enrollments.length; j++) {
                        const enrollment = this.enrollments[j];
                        if (course.id === enrollment.course_id) {
                            course.enrolled = true;
                            break;
                        }
                    }

                    let found = false;
                    for (let i = 0; i < this.courses.length; i++) {
                        const c = this.courses[i];
                        if (c.id === course.id) {
                            found = true;
                            break;
                        }
                    }

                    if (!found) {
                        this.courses.push(course);
                        this.course(course.id);
                    }
                }).catch(err => {
                    console.log(err);
                    if (err.response.status === 404) {
                        this.$notify('error', 'Training', 'Invalid Training Code.  Please check the code and try again. Please note this is not a search field.', {duration: 5000, permanent: false});
                    } else {
                        window.alert('An error occurred.  If you need additional help please contact us at support@my247ed.com.');
                    }
                });
                this.trainingCode = '';

            }
        },
        watch: {
            '$route.query'() {
                this.syncFromRoute();
            }
        },
        mounted: function() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .options {
        position: relative;
        width: 360px;
        transition: width 100ms;

        padding-top: 3.8rem;

        .sub-header {
            font-weight: bold !important;
            cursor: default !important;
            color: #4a4a4a !important;
            padding: 0.5rem 2rem 0.3rem !important;
            font-size: 0.8rem !important;
            text-transform: uppercase;
            background-color: #f3f3f5 !important;
        }

        section {
            padding: 1rem 2rem;

            .field {
                margin: 3px;
            }
        }
    }

    .clickable {
        cursor: pointer !important;
    }

    h2.catalog-heading {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1.5rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

    .audience-tile {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 1.5rem 2rem;
        cursor: pointer;

        .meta {
            font-weight: normal;
            font-size: 0.8rem;
            color: #737881;
        }

        &:hover {
            box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.6);
        }
    }
</style>

<style lang="scss">
    .course-tabs {
        .audience-filters {
            .label {
                color: white;
                font-size: 0.85rem;
            }
        }
    }

    .notifyModal {
        z-index: 120;

        ul.model-menu {
            list-style: none;
            margin: 0 0 0 -0.75rem;
            padding: 0;

            li {
                margin: 0;
                padding: 0.2rem 0;
                border-top: 1px solid #eee;
                font-weight: bold;

                &:first-child {
                    border: none;
                }

                > a {
                    display: block;
                    color: #333;
                    padding: 0.5rem 0 0.5rem 0.8rem;

                    &:hover {
                        cursor: pointer;
                        border-radius: 5px;
                        background-color: #336699;
                        color: #ffffff !important;

                        i {
                            color: white !important;
                        }
                    }
                }
            }
        }
    }
</style>
